import DashboardLayoutVue from '@/layouts/dashboard.vue';
import { createRouter, createWebHistory, type RouteMeta } from 'vue-router';

interface IRouteMeta {
  title: string,
  role: string[]
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/home',
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: {
        title: 'Login',
      } as RouteMeta & IRouteMeta,
    },
    {
      path: '/',
      component: DashboardLayoutVue,
      redirect: '/home',
      meta: {
        title: 'Dashboard',
        role: ['super'],
      },
      children: [
        {
          path: 'home',
          name: 'home',
          component: () => import('@/views/dashboard/examples/Home.vue'),
          meta: {
            title: 'Home',
            role: ['super'],
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'task',
          name: 'tasks_index',
          component: () => import('@/views/dashboard/examples/tasks/Index.vue'),
          meta: {
            title: 'Tasks',
            role: ['super', 'admin', 'editor'], // Readers cannot modify tasks
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/users',
          name: 'user_index',
          component: () => import('@/views/users/user-list.vue'),
          meta: {
            title: 'Users',
            role: ['super', 'admin', 'editor', 'reader'], // All roles can view users
          } as RouteMeta & IRouteMeta,
        },
        {
          path: '/users/quizzes',
          name: 'quizzes_index',
          component: () => import('@/views/quizzes/Quizzes.vue'),
          meta: {
            title: 'User Quizzes',
            role: ['super', 'admin', 'editor', 'reader'], // All roles can view quizzes
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/users/engagement-quota',
          name: 'engagement-quota_index',
          component: () => import('@/views/engQuota/quotaList.vue'),
          meta: {
            title: 'Engagement Quota',
            role: ['super', 'admin', 'editor', 'reader'], // All roles can view engagement
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/users/detail/:id',
          name: 'user_detail',
          component: () => import('@/views/users/user-detail.vue'), 
          props: true, 
          meta: {
            title: 'User Detail',
            role: ['super', 'admin', 'editor', 'reader'], // All roles can view user details
          } as RouteMeta & IRouteMeta,
        },
        {
          path: 'settings',
          name: 'settings_index',
          component: () => import('@/views/dashboard/examples/settings/Index.vue'),
          meta: {
            title: 'Settings',
            role: ['super', 'admin'], // Only super and admin can access settings
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/plans',
          name: 'plans_index',
          component: () => import('@/views/plans/web-plan.vue'),
          meta: {
            title: 'Plans',
            role: ['super', 'admin', 'editor', 'reader'], // All roles can view plans
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/plans/add',
          name: 'add_plan',
          component: () => import('@/views/plans/addPlan.vue'),
          meta: {
            title: 'Add Plan',
            role: ['super', 'admin'], // Only super and admin can add plans
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/plans/edit/:id',
          name: 'edit_plan',
          component: () => import('@/views/plans/addPlan.vue'),
          props: true,
          meta: {
            title: 'Edit Plan',
            role: ['super', 'admin'], // Only super and admin can edit plans
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/languages',
          name: 'languages',
          component: () => import('@/views/language/list.vue'),
          meta: {
            title: 'Languages',
            role: ['super', 'admin', 'editor', 'reader'], // All roles can view languages
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/currencies',
          name: 'currencies',
          component: () => import('@/views/country/country-list.vue'),
          meta: {
            title: 'Currencies',
            role: ['super', 'admin', 'editor', 'reader'], // All roles can view currencies
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/assets',
          name: 'files',
          component: () => import('@/views/files/asset-list.vue'),
          meta: {
            title: 'Assets',
            role: ['super', 'admin', 'editor', 'reader'], // All roles can view assets
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/features',
          name: 'features',
          component: () => import('@/views/features/feature-list.vue'),
          meta: {
            title: 'Features',
            role: ['super', 'admin', 'editor', 'reader'], // All roles can view features
          } as RouteMeta & IRouteMeta
        },
        {
          path:'/upcoming/features',
          name: 'upcoming_features',
          component: () => import('@/views/upcoming-feature/Index.vue'),
          meta: {
            title: 'Upcoming Features',
            role: ['super', 'admin', 'editor', 'reader'], // All roles can view upcoming features
          } as RouteMeta & IRouteMeta
        }
        ,
        {
          path: '/guide/resources',
          name: 'guide_resources',
          component: () => import('@/views/guide-resource/Index.vue'),
          meta: {
            title: 'Guide Resources',
            role: ['super', 'admin', 'editor', 'reader'],
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/notifications',
          name: 'notifications',
          component: () => import('@/views/notifications/Index.vue'),
          meta: {
            title: 'Notifications',
            role: ['super', 'admin', 'editor', 'reader'],
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/inspiration/quizzes',
          name: 'inspiration_quizzes',
          component: () => import('@/views/inspiration/InspirationQuizzes.vue'),
          meta: {
            title: 'Inspiration Quizzes',
            role: ['super', 'admin', 'editor', 'reader'],
          } as RouteMeta & IRouteMeta
        }
        ,
        {
          path: '/quiz/tips',
          name: 'quiz_tips',
          component: () => import('@/views/quiz-tips/Index.vue'),
          meta: {
            title: 'Quiz Tips',
            role: ['super', 'admin', 'editor', 'reader'],
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/inspiration/quizzes/add',
          name: 'inspiration_quizzes_add',
          component: () => import('@/views/inspiration/AddInspiration.vue'),
          meta: {
            title: 'Add Inspiration Quiz',
            role: ['super', 'admin', 'editor'],
          } as RouteMeta & IRouteMeta
        },
        {
          path: '/inspiration/quizzes/edit/:id',
          name: 'inspiration_quizzes_edit',
          component: () => import('@/views/inspiration/EditInspiration.vue'),
          props: true,
          meta: {
            title: 'Edit Inspiration Quiz',
            role: ['super', 'admin', 'editor'],
          } as RouteMeta & IRouteMeta
        }
        // Add more routes with proper role permissions
        // {
        //   path: '/templates',
        //   name: 'templates_index',
        //   component: () => import('@/views/templates/template-list.vue'),
        //   meta: {
        //     title: 'Templates',
        //     role: ['super', 'admin', 'editor', 'reader'],
        //   } as RouteMeta & IRouteMeta
        // },
        // {
        //   path: '/integrations',
        //   name: 'integrations_index',
        //   component: () => import('@/views/integrations/integration-list.vue'),
        //   meta: {
        //     title: 'Integrations',
        //     role: ['super', 'admin', 'editor', 'reader'],
        //   } as RouteMeta & IRouteMeta
        // },
        // {
        //   path: '/error-logs',
        //   name: 'error_logs_index',
        //   component: () => import('@/views/error-logs/error-log-list.vue'),
        //   meta: {
        //     title: 'Error Logs',
        //     role: ['super', 'admin', 'editor'], // Readers cannot access error logs
        //   } as RouteMeta & IRouteMeta
        // },
        // {
        //   path: '/roles',
        //   name: 'roles_index',
        //   component: () => import('@/views/roles/role-list.vue'),
        //   meta: {
        //     title: 'Roles & Permissions',
        //     role: ['super', 'admin'], // Only super and admin can manage roles
        //   } as RouteMeta & IRouteMeta
        // },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/404.vue'),
      meta: {
        title: 'Page Not Found',
      } as RouteMeta & IRouteMeta,
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('@/views/Unauthorized.vue'),
      meta: {
        title: 'Unauthorized',
      } as RouteMeta & IRouteMeta,
    },
  ]
});

// Enhanced navigation guard with better error handling
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('BearerToken'); 
  const userRaw = localStorage.getItem('user');
  const user = userRaw ? JSON.parse(userRaw) : null;
  const userRole = user?.role;
  
  // Redirect unauthenticated users to login
  if (!token && to.name !== 'login') {
    return next({ name: 'login' });
  }

  // Redirect authenticated users away from login
  if (token && to.name === 'login') {
      return next({ name: 'home' });
  }
  
  if (token && to.name === 'home') {
    if (userRole !== 'super') {
      return next({ name: 'user_index' });
    }
  }
  
  // Check route permissions
  const allowedRoles = (to.meta as unknown as IRouteMeta)?.role;
  
  if (allowedRoles) {
    if (!allowedRoles.includes(userRole)) {
      console.warn(`User with role '${userRole}' attempted to access '${String(to.name)}' which requires roles: [${allowedRoles.join(', ')}]`);
      return next({ name: 'unauthorized' });
    }
  }
  
  // Update document title
  document.title = to.meta.title as string || 'Admin Dashboard';
  
  return next();
});

// Optional: Add route guard for better error handling
router.onError((error) => {
  console.error('Router error:', error);
});

export default router;